// packages/shared/src/utils/cnpj.ts — CNPJ normalization & formatting
//
// Single source of truth for converting between formatted CNPJ
// (`01.345.678/0001-12`) and digits-only (`01345678000112`). Used at every
// trust boundary that crosses the URL or DB layer. Display layer uses
// `tryFormatCnpj` to render.
//
// Compat-window contract: `toDigitsCnpj` accepts both formats during the
// migration. Once the PG CHECK constraint lands and the frontend sends only
// digits-only, the formatted-input branch becomes dead code and can be
// deleted.

export function toDigitsCnpj(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;
  const digits = raw.replace(/%2F/gi, "/").replace(/\D/g, "");
  return digits.length === 14 ? digits : raw.trim();
}

export function formatCnpj(digits: string): string {
  const d = digits.replace(/\D/g, "");
  if (d.length !== 14) return digits;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export function tryFormatCnpj(input: string): string {
  const d = input.replace(/\D/g, "");
  return d.length === 14 ? formatCnpj(d) : input;
}

// ---------------------------------------------------------------------------
// Entity documents (CPF *or* CNPJ)
//
// ponytail: `toDigitsCnpj` returns its input UNCHANGED when the digit count
// isn't 14, so a formatted CPF (`269.253.728-92`) survives it still masked.
// The ownership graph keys nodes on a document that may be either a CPF (11)
// or a CNPJ (14), so it needs a normalizer that rejects rather than passes
// through. Do not "simplify" these into toDigitsCnpj.
// ---------------------------------------------------------------------------

/** Digits-only for a CPF (11) or CNPJ (14). `undefined` for anything else. */
export function toEntityDoc(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;
  const digits = raw.replace(/%2F/gi, "/").replace(/\D/g, "");
  return digits.length === 11 || digits.length === 14 ? digits : undefined;
}

export function formatCpf(digits: string): string {
  const d = digits.replace(/\D/g, "");
  if (d.length !== 11) return digits;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** Format a CPF or CNPJ for display; anything else passes through unchanged. */
export function tryFormatDoc(input: string): string {
  const d = input.replace(/\D/g, "");
  if (d.length === 11) return formatCpf(d);
  if (d.length === 14) return formatCnpj(d);
  return input;
}
